import { useQuery } from '@tanstack/react-query'
import { format as formatDateFns } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import apiClient from '@/api/client'
import { getStoredUserRole } from '@/lib/staff-roles'
import type { PnLReport } from '@/types'
import { CategorySpendList } from './CategorySpendList'
import { CostingCheckCard } from './CostingCheckCard'
import { useMoney } from './moneyUi'
import { PnlBridge } from './PnlBridge'
import { PeriodBreakdownTable } from './PeriodBreakdownTable'
import { PeriodResultChart, type ResultPoint } from './PeriodResultChart'
import { PnlStatement } from './PnlStatement'
import { cogsBasisNote, lossCount, periodPoint, stockCategoriesNote } from './pnlStatementLogic'
import { useExpenseCategoryDefs } from './use-expense-category-defs'
import {
  PNL_GRANULARITIES,
  PNL_GRANULARITY_LABELS,
  granularityBlockedReason,
  resolveGranularity,
  type PnlGranularity,
} from './expenseDashboardLogic'
import { cn, formatDateHuman, formatDateTimeHuman } from '@/lib/utils'

/** How the grain reads in a sentence: "One row per week". */
const GRAIN_NOUN: Record<PnlGranularity, string> = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
}

/** The row label in the table and the tooltip. */
function formatPeriod(dateStr: string, grain: PnlGranularity): string {
  try {
    if (grain === 'hourly') return formatDateTimeHuman(dateStr)
    if (grain === 'weekly') return `Week of ${formatDateHuman(dateStr)}`
    if (grain === 'monthly') return formatDateFns(new Date(dateStr), 'MMMM yyyy')
    return formatDateHuman(dateStr)
  } catch {
    return dateStr
  }
}

/** The short label under the axis, where the weekday would crowd. */
function formatTick(dateStr: string, grain: PnlGranularity): string {
  try {
    const d = new Date(dateStr)
    if (grain === 'hourly') return formatDateFns(d, 'd MMM, ha')
    if (grain === 'monthly') return formatDateFns(d, 'MMM yyyy')
    return formatDateFns(d, 'd MMM')
  } catch {
    return dateStr
  }
}

type Props = {
  pnl?: PnLReport
  period: string
  setPeriod: (p: string) => void
  /** ISO bounds of the page's period bar (F-FE-N129) — no pickers of its own any more. */
  from: string
  to: string
  /** Days in the page's period bar — decides which grains are offered. */
  rangeDays: number
  /** The period bar's label, for the statement's heading. */
  periodLabel: string
  /** Opens the ledger filtered to a category for the same period. */
  onDrillCategory: (slug: string) => void
  /** Opens the ledger for the period, unfiltered. */
  onOpenLedger: () => void
}

/**
 * The P&L (F-FE-N164), laid out in the order it is read: the four figures that
 * answer "did we make money", the statement that shows how, where the
 * operating expenses went, then the same story per period as a chart and as
 * the table behind it.
 */
export function ExpensePnLTab({
  pnl,
  period,
  setPeriod,
  from,
  to,
  rangeDays,
  periodLabel,
  onDrillCategory,
  onOpenLedger,
}: Props) {
  const money = useMoney()

  // Pull projected gross margin for the same window via reports/v2/overview.
  // Kept as an independent query (not derived from PnLReport) because the
  // legacy /admin/reports/pnl endpoint uses tax-included revenue keyed off
  // completed_at, while reports/v2/overview uses net-of-tax sales keyed off
  // created_at. The costing card labels its figures as a projection so the
  // reader can hold them against the statement rather than us silently fudging
  // one to match.
  //
  // F-RPT-N18: fall back to the window the server actually resolved and echoed
  // back on the report, so the card renders on the default view too.
  const effFrom = from || pnl?.from || ''
  const effTo = to || pnl?.to || ''
  const projectedQuery = useQuery({
    enabled: Boolean(effFrom && effTo),
    queryKey: ['reports-v2', 'overview', 'pnl-projected', effFrom, effTo],
    queryFn: () => apiClient.getReportsOverview(effFrom, effTo),
    staleTime: 30_000,
  })
  const projected = projectedQuery.data?.data

  // F-RPT-N42: on the purchases basis the note under the statement names the
  // categories that count as stock; defs are only fetched on that basis.
  const onPurchases = pnl?.summary?.cogs_basis === 'purchases' || pnl?.summary?.cogs_basis === 'mixed'
  const { data: categoryDefs = [] } = useExpenseCategoryDefs(onPurchases)

  // The parent already resolves this against the range; re-resolving keeps
  // the component honest if it is ever rendered with a stale value.
  const grain = resolveGranularity(period, rangeDays)

  const summary = pnl?.summary
  const points = (pnl?.rows ?? []).map(periodPoint)
  const anyRefunds = points.some(p => p.refunds > 0.005)
  const resultLabel = anyRefunds ? 'Net profit after refunds' : 'Net profit'
  const losses = lossCount(points, anyRefunds)

  const chartPoints: ResultPoint[] = points.map(p => ({
    tick: formatTick(p.period, grain),
    label: formatPeriod(p.period, grain),
    sales: p.netSales,
    cogs: p.cogs,
    opex: p.opex,
    result: anyRefunds ? p.netAfterRefunds : p.netProfit,
    notes: [
      { label: 'Orders', value: String(p.orders) },
      ...(p.refunds > 0.005 ? [{ label: 'Refunds (excl. tax)', value: money.full(-p.refunds) }] : []),
    ],
  }))

  return (
    <div className="space-y-6">
      {summary && <PnlBridge summary={summary} onOpenLedger={onOpenLedger} />}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Flex column so the statement card (h-full) takes what is left after
            the notes, instead of filling the row and pushing the notes into
            the card below. */}
        <div className="flex flex-col gap-3 xl:col-span-2">
          <div className="min-h-0 flex-1">
            <PnlStatement summary={summary} periodLabel={periodLabel} />
          </div>
          {cogsBasisNote(summary?.cogs_basis, effFrom, effTo) && (
            <p className="text-sm text-muted-foreground">{cogsBasisNote(summary?.cogs_basis, effFrom, effTo)}</p>
          )}
          {stockCategoriesNote(summary?.cogs_basis, categoryDefs) && (
            <p className="text-sm text-muted-foreground" data-testid="stock-categories-note">
              {stockCategoriesNote(summary?.cogs_basis, categoryDefs)}
            </p>
          )}
        </div>
        <Card className="h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Operating expenses by category</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Select a category to see its entries in the ledger.</p>
          </CardHeader>
          <CardContent className="pt-0">
            <CategorySpendList categories={pnl?.expense_breakdown} onDrill={onDrillCategory} />
          </CardContent>
        </Card>
      </div>

      {projected && summary?.cogs_basis !== 'purchases' && (
        <CostingCheckCard projected={projected} canEditCosting={getStoredUserRole() === 'admin'} />
      )}

      {/* F-FE-N141: the grain lives ON the two blocks it actually changes. */}
      <PeriodResultChart
        title={`Sales, costs and profit by ${GRAIN_NOUN[grain]}`}
        description={
          points.length === 0
            ? 'Sales count on the day an order is completed; expenses on the expense date.'
            : `${losses === 0 ? `No ${GRAIN_NOUN[grain]} ended in a loss` : `${losses} of ${points.length} ${GRAIN_NOUN[grain]}s ended in a loss`}. Sales count on the day an order is completed; expenses on the expense date.`
        }
        points={chartPoints}
        salesLabel="Net sales"
        resultLabel={resultLabel}
        action={<GrainSwitch grain={grain} rangeDays={rangeDays} onChange={setPeriod} />}
      />

      <PeriodBreakdownTable
        points={points}
        grainNoun={GRAIN_NOUN[grain]}
        formatPeriod={iso => formatPeriod(iso, grain)}
        showRefunds={anyRefunds}
      />
    </div>
  )
}

function GrainSwitch({
  grain,
  rangeDays,
  onChange,
}: {
  grain: PnlGranularity
  rangeDays: number
  onChange: (g: string) => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 self-start rounded-lg bg-muted/60 p-1" role="group" aria-label="Group by">
      {PNL_GRANULARITIES.map(g => {
        const blocked = granularityBlockedReason(g, rangeDays)
        const btn = (
          <button
            key={g}
            type="button"
            disabled={!!blocked}
            aria-pressed={grain === g}
            onClick={() => onChange(g)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              grain === g
                ? 'bg-background text-foreground shadow-sm'
                : blocked
                  ? 'cursor-not-allowed text-muted-foreground/50'
                  : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {PNL_GRANULARITY_LABELS[g]}
          </button>
        )
        if (!blocked) return btn
        return (
          <Tooltip key={g}>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={0}>
                {btn}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{blocked}</TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}
